import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from './config';
import './styles/Login.css';

export function VerificationCode({ usuarioId, correo, onVerificationSuccess, onCancel }) {
    const [codigo, setCodigo] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [timer, setTimer] = useState(60);
    const inputRefs = useRef([]);

    useEffect(() => {
        if (timer > 0) {
            const interval = setInterval(() => setTimer(timer - 1), 1000);
            return () => clearInterval(interval);
        }
    }, [timer]);

    const distributeDigits = (digits, startIndex = 0) => {
        const newCodigo = [...codigo];
        digits.forEach((digit, i) => {
            const targetIndex = startIndex + i;
            if (targetIndex < 6) newCodigo[targetIndex] = digit;
        });
        setCodigo(newCodigo);

        const filledCount = startIndex + digits.length;
        const focusIndex = filledCount >= 6 ? 5 : filledCount;
        const focusInput = inputRefs.current[focusIndex];
        if (focusInput) {
            setTimeout(() => focusInput.focus(), 0);
        }
    };

    const handleChange = (index, value) => {
        if (!value) {
            const newCodigo = [...codigo];
            newCodigo[index] = '';
            setCodigo(newCodigo);
            return;
        }

        // Multi-character input: paste or SMS autofill on mobile
        if (value.length > 1) {
            const digits = value.replace(/\D/g, '').substring(0, 6).split('');
            distributeDigits(digits, 0);
            return;
        }

        // Single digit
        if (isNaN(value)) return;
        const newCodigo = [...codigo];
        newCodigo[index] = value;
        setCodigo(newCodigo);

        // Auto-focus next input
        if (index < 5) {
            const nextInput = inputRefs.current[index + 1];
            if (nextInput) nextInput.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !codigo[index] && index > 0) {
            const prevInput = inputRefs.current[index - 1];
            if (prevInput) {
                prevInput.focus();
                const newCodigo = [...codigo];
                newCodigo[index - 1] = '';
                setCodigo(newCodigo);
            }
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const clipboardData = e.clipboardData.getData('text');
        const digits = clipboardData.replace(/\D/g, '').substring(0, 6).split('');
        if (digits.length > 0) {
            distributeDigits(digits, 0);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const fullCode = codigo.join('');
        if (fullCode.length !== 6) {
            setError('Por favor ingrese el código completo de 6 dígitos');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await fetch(`${API_BASE_URL}/api/auth/verify-two-factor`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ usuarioId, codigo: fullCode }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                onVerificationSuccess(data);
            } else {
                setError(data.error || 'Código inválido o expirado');
            }
        } catch (err) {
            setError('Error de conexión con el servidor');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-box">
                <h1>Verificación</h1>
                <p style={{ textAlign: 'center', color: '#64748b', marginBottom: '24px' }}>
                    Hemos enviado un código de 6 dígitos a:<br />
                    <strong style={{ color: '#0f172a' }}>{correo}</strong>
                </p>

                <form onSubmit={handleSubmit}>
                    <div className="digit-input-container">
                        {codigo.map((digit, index) => (
                            <input
                                key={index}
                                ref={(el) => { inputRefs.current[index] = el; }}
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                                className="digit-input"
                                value={digit}
                                onChange={(e) => handleChange(index, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                onPaste={handlePaste}
                                disabled={loading}
                            />
                        ))}
                    </div>

                    {error && <div className="error-message">{error}</div>}

                    <button
                        type="submit"
                        className="login-button"
                        disabled={loading || codigo.some(d => !d)}
                    >
                        {loading ? 'Verificando...' : 'Confirmar Identidad'}
                    </button>

                    <div style={{ marginTop: '32px', textAlign: 'center' }}>
                        {timer > 0 ? (
                            <p style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
                                Podrás solicitar un nuevo código en <strong>{timer}s</strong>
                            </p>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setTimer(60)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#4f46e5',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem'
                                }}
                            >
                                Reenviar Código
                            </button>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={onCancel}
                        style={{
                            marginTop: '16px',
                            background: 'none',
                            border: 'none',
                            color: '#94a3b8',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            width: '100%'
                        }}
                    >
                        ← Volver al Login
                    </button>
                </form>
            </div>
        </div>
    );
}
